import { campaignsApi } from "@/api/campaigns.api";
import { Campaign, CampaignContext, CampaignCreation } from "@/types/campaign";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { queryClient } from "./queryClient";
import { storeToRefs } from "pinia";
import { invitationApi } from "@/api/invitation.api";
import { useToast } from "vue-toastification";

export async function fetchCampaigns(organisationId: string, spaceId: string) {
  return queryClient.fetchQuery({
    queryKey: ['campaigns', { organisationId: organisationId, spaceId: spaceId }],
    queryFn: async () => await campaignsApi.getAll(organisationId, spaceId, false),
  });
}

export async function fetchCampaign(organisationId: string, campaignId: string) {
  return queryClient.fetchQuery({
    queryKey: ['campaigns', { organisationId: organisationId, campaignId: campaignId }],
    queryFn: async () => {
      const campaign = await campaignsApi.get(campaignId, false);
      queryClient.setQueryData(['campaigns', { organisationId: organisationId, spaceId: campaign.campaign.parent_workspace_id, campaignId: campaignId }], campaign);
      return campaign;
    },
  });
}

export function useCampaigns() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId }],
    queryFn: async () => {
      const campaigns = await campaignsApi.getAll(toValue(appStore.currentOrganisationId), toValue(appStore.currentSpaceId), false);
      campaigns.forEach((c) => {
        queryClient.setQueryData(['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId, campaignId: c.id }], c);
      });
      return campaigns;
    },
    enabled: !!toValue(appStore.currentOrganisationId) && !!toValue(appStore.currentSpaceId)
  });
}

export function useCampaign() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId, campaignId: appStore.currentCampaignId }],
    queryFn: () => {
      return campaignsApi.get(toValue(appStore.currentCampaignId), false);
    },
    enabled: !!toValue(appStore.currentCampaignId) && !!toValue(appStore.currentOrganisationId),
  });
}

export function useArchivedCampaigns() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['archived-campaigns', appStore.currentOrganisationId],
    queryFn: () => campaignsApi.getArchivedCampaigns(toValue(appStore.currentOrganisationId)),
    enabled: !!toValue(appStore.currentOrganisationId)
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { organisationId: string, newCampaign: CampaignCreation }) => campaignsApi.add(data.organisationId, data.newCampaign),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: variables.organisationId, spaceId: variables.newCampaign.campaign.parent_workspace_id } ] });
    }
  });
}

export function useEditCampaign() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: { context: CampaignContext, campaign: Partial<Campaign>, auto_activate?: boolean }) => campaignsApi.edit(data.context, data.campaign, data.auto_activate),
    onSuccess: (data, variables: { context: CampaignContext, campaign: Partial<Campaign>, auto_activate?: boolean } ) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: variables.context.organisationId, spaceId: variables.context.spaceId }] });
      toast.success(t('app.campaign.settings.toast.save'));
    },
    onError: (error) => {
      toast.error('Error');
    },
  });
}

export function useArchiveCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { organisationId: string, spaceId: string, campaignId: string }) => campaignsApi.archive(data.organisationId, data.spaceId, data.campaignId),
    onSuccess: (data, variables: { organisationId: string, spaceId: string, campaignId: string }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: variables.organisationId }] });
      queryClient.invalidateQueries({ queryKey: ['archivedCampaigns'] });
    },
  });
}

export function useRestoreCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { context: { organisationId: string, campaignId: string }, spaceId: string }) => campaignsApi.restore(data.context.organisationId, data.context.campaignId, data.spaceId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: variables.context.organisationId, spaceId: variables.spaceId }] });
      queryClient.invalidateQueries({ queryKey: ['archivedCampaigns'] });
    },
  });
}

export function useTogglePublicInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { context: CampaignContext, enabled: boolean }) => campaignsApi.toggleInvitation(data.context, data.enabled),
    onSuccess: (data, variables: { context: CampaignContext, spaceId: string, enabled: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', variables.context], refetchType: 'active' });
    },
  });
}